import { authedFetch } from "../../../auth/authedFetch";
import { CHAT_API_BASE_URL } from "../../../services/chatService";

/**
 * Sends a reply the owner has explicitly approved (SGL-244).
 *
 * The paired tool, `ghl_send_message`, only drafts — this is the one call in
 * the flow that actually puts a message in a contact's inbox, and it exists so
 * that step is a person's click rather than a model's tool call.
 *
 * authedFetch rather than the shared axios instance on purpose: the global
 * axios interceptor force-logs-out on 401/403, and a GHL-side failure must not
 * end the user's session (the same trap that bit the Graph API calls). The
 * backend maps GHL reasons to 404/409/502 and reserves 401 for genuinely
 * missing app auth.
 */
export interface SendApprovedMessageArgs {
  customerId: string;
  contactId: string;
  type: "SMS" | "EMAIL";
  message: string;
  subject?: string;
  idToken?: string;
}

export interface SendApprovedMessageResult {
  status: "sent";
  message_id: string;
  type: "SMS" | "EMAIL";
  contact_id: string;
}

export async function sendApprovedGhlMessage(
  args: SendApprovedMessageArgs
): Promise<SendApprovedMessageResult> {
  const base = (CHAT_API_BASE_URL || "").replace(/\/$/, "");
  if (!base) {
    throw new Error("Chat API URL is not configured (REACT_APP_CHAT_API_BASE_URL).");
  }
  if (!args.customerId || !args.contactId || !args.message) {
    throw new Error("Missing recipient or message.");
  }

  const response = await authedFetch(
    `${base}/ghl/messages/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customer_id: args.customerId,
        contact_id: args.contactId,
        type: args.type,
        message: args.message,
        subject: args.subject || undefined,
      }),
    },
    args.idToken
  );

  if (!response.ok) {
    // Surface the backend's own wording — it distinguishes "not connected"
    // from "needs reauthorization" from "GHL is down", and the owner needs to
    // know which before deciding what to do about an unsent reply.
    let detail = "";
    try {
      const body = await response.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {
      /* non-JSON error body — fall through to the generic message */
    }
    throw new Error(detail || `Couldn't send the message (${response.status}).`);
  }

  return (await response.json()) as SendApprovedMessageResult;
}
